import { SavaquanHttpError, ErrorResponse } from '../utils/http-error';
import { getUserTokenOrRedirect, isAuthRedirectError } from './auth.service';
import { NavigateFunction } from 'react-router-dom';

export interface SavaquanFetchOptions extends RequestInit {
  /**
   * Navigation function for automatic authentication and redirects
   * Required for automatic auth - will call getUserTokenOrRedirect by default
   */
  navigate: NavigateFunction;
  /**
   * Skip automatic authorization (for public endpoints)
   */
  skipAuth?: boolean;
  /**
   * Custom authorization token to use instead of getting from localStorage
   */
  customToken?: string;
}

export interface SavaquanFetchOptionsNoAuth extends RequestInit {
  /**
   * Skip automatic authorization (for public endpoints)
   */
  skipAuth: true;
}

/**
 * Enhanced fetch wrapper that handles errors and wraps them in SavaquanHttpError
 * @param input - URL or Request object
 * @param options - SavaquanFetchOptions with automatic auth (navigate required) or SavaquanFetchOptionsNoAuth (skipAuth: true)
 * @returns Promise<Response> - The response if successful
 * @throws SavaquanHttpError - Wrapped error with standardized error response
 */
export async function savaquanFetch(
  input: RequestInfo | URL, 
  options: SavaquanFetchOptions | SavaquanFetchOptionsNoAuth
): Promise<Response> {
  try {
    // Prepare the request options with automatic authorization
    const requestOptions = await prepareRequestOptions(options);
    
    const response = await fetch(input, requestOptions);
    
    if (!response.ok) {
      await handleErrorResponse(response);
    }
    
    return response;
  } catch (error) {
    // If it's already a SavaquanHttpError, re-throw it
    if (error instanceof SavaquanHttpError) {
      throw error;
    }

    // We're already redirecting to /login; callers suppress this one by type.
    if (isAuthRedirectError(error)) {
      throw error;
    }
    
    // Handle network errors or other fetch failures
    const errorResponse: ErrorResponse = {
      message: error instanceof Error ? error.message : 'An unknown error occurred',
      details: 'Network error or fetch failure',
      statusCode: 0, // Network errors don't have HTTP status codes
      timestamp: new Date()
    };
    
    throw new SavaquanHttpError(errorResponse);
  }
}

/**
 * Prepares request options with automatic authorization
 * @param options - SavaquanFetchOptions or SavaquanFetchOptionsNoAuth
 * @returns Promise<RequestInit> - Prepared request options
 */
async function prepareRequestOptions(options: SavaquanFetchOptions | SavaquanFetchOptionsNoAuth): Promise<RequestInit> {
  // If skipAuth is true, return without authentication
  if ('skipAuth' in options && options.skipAuth) {
    const { skipAuth, ...requestInit } = options;
    return addContentTypeForJsonRequests(requestInit);
  }
  
  // Cast to SavaquanFetchOptions since we know skipAuth is not true
  const { navigate, skipAuth, customToken, ...requestInit } = options as SavaquanFetchOptions;
  
  try {
    // Get the authorization token (this is now the default behavior)
    const token = customToken || await getUserTokenOrRedirect(navigate);
    
    // Prepare headers
    const headers = new Headers(requestInit.headers);
    
    // Only add Authorization header if it doesn't already exist
    if (!headers.has('Authorization') && token) {
      headers.set('Authorization', `Bearer ${token}`);
    }
    
    const requestWithHeaders = {
      ...requestInit,
      headers
    };
    
    return addContentTypeForJsonRequests(requestWithHeaders);
  } catch (error) {
    // We're already redirecting to /login; let this propagate untouched so
    // callers can suppress it rather than surfacing a spurious 401 toast.
    if (isAuthRedirectError(error)) {
      throw error;
    }
    // If authentication fails, wrap it in our error type
    const errorResponse: ErrorResponse = {
      message: 'Authentication failed',
      details: error instanceof Error ? error.message : 'Failed to get authentication token',
      statusCode: 401,
      timestamp: new Date()
    };
    
    throw new SavaquanHttpError(errorResponse);
  }
}

/**
 * Automatically adds Content-Type header for JSON requests
 * @param requestInit - Request initialization object
 * @returns RequestInit with appropriate Content-Type header
 */
function addContentTypeForJsonRequests(requestInit: RequestInit): RequestInit {
  // Only add Content-Type for requests with a body that could be JSON
  if (!requestInit.body) {
    return requestInit;
  }
  
  const headers = new Headers(requestInit.headers);
  
  // Don't override existing Content-Type header
  if (headers.has('Content-Type')) {
    return { ...requestInit, headers };
  }
  
  // Check if the body is likely JSON (string that looks like JSON or already serialized)
  const isJsonBody = typeof requestInit.body === 'string' && 
    (requestInit.body.startsWith('{') || requestInit.body.startsWith('['));
  
  // Don't set Content-Type for FormData (browser will set it automatically with boundary)
  const isFormData = requestInit.body instanceof FormData;
  
  // Set Content-Type to application/json for string bodies that look like JSON
  // and for methods that typically send JSON
  if (isJsonBody && !isFormData) {
    headers.set('Content-Type', 'application/json');
  }
  
  return {
    ...requestInit,
    headers
  };
}

/**
 * Handles error responses by parsing the response body and creating appropriate ErrorResponse
 * @param response - The failed HTTP response
 * @throws SavaquanHttpError - Always throws with parsed or fallback error data
 */
async function handleErrorResponse(response: Response): Promise<never> {
  let errorResponse: ErrorResponse;
  
  try {
    // Try to parse the response body as JSON
    const responseBody = await response.text();
    
    if (responseBody) {
      try {
        const parsedBody = JSON.parse(responseBody);
        
        // Check if the response body has the expected ErrorResponse shape
        errorResponse = {
          message: parsedBody.message || parsedBody.Message || `HTTP ${response.status}: ${response.statusText}`,
          details: parsedBody.details || parsedBody.Details || responseBody,
          statusCode: response.status,
          timestamp: parsedBody.timestamp || parsedBody.Timestamp 
            ? new Date(parsedBody.timestamp || parsedBody.Timestamp) 
            : new Date()
        };
      } catch (jsonError) {
        // Response body is not valid JSON, use it as details
        errorResponse = {
          message: `HTTP ${response.status}: ${response.statusText}`,
          details: responseBody,
          statusCode: response.status,
          timestamp: new Date()
        };
      }
    } else {
      // No response body
      errorResponse = {
        message: `HTTP ${response.status}: ${response.statusText}`,
        details: undefined,
        statusCode: response.status,
        timestamp: new Date()
      };
    }
  } catch (bodyReadError) {
    // Failed to read response body
    errorResponse = {
      message: `HTTP ${response.status}: ${response.statusText}`,
      details: 'Failed to read response body',
      statusCode: response.status,
      timestamp: new Date()
    };
  }
  
  throw new SavaquanHttpError(errorResponse);
}

/**
 * Convenience wrapper for JSON responses that automatically parses the response
 * @param input - URL or Request object
 * @param options - SavaquanFetchOptions with automatic auth (navigate required) or SavaquanFetchOptionsNoAuth (skipAuth: true)
 * @returns Promise<T> - Parsed JSON response
 * @throws SavaquanHttpError - Wrapped error with standardized error response
 */
export async function savaquanFetchJson<T = any>(
  input: RequestInfo | URL, 
  options: SavaquanFetchOptions | SavaquanFetchOptionsNoAuth
): Promise<T> {
  const response = await savaquanFetch(input, options);
  
  try {
    return await response.json() as T;
  } catch (error) {
    const errorResponse: ErrorResponse = {
      message: 'Failed to parse JSON response',
      details: error instanceof Error ? error.message : 'Invalid JSON in response body',
      statusCode: response.status,
      timestamp: new Date()
    };
    
    throw new SavaquanHttpError(errorResponse);
  }
}
